import { supabase } from '../lib/supabase'
import { supabaseAdmin } from '../lib/supabaseAdmin'

class AdminUserService {
  async updateUserRole(userId: string, role: 'user' | 'admin' | 'super_admin'): Promise<void> {
    if (!supabaseAdmin) {
      throw new Error('Supabase admin client is not configured')
    }

    try {
      const { error } = await supabaseAdmin
        .from('profiles')
        .update({ role })
        .eq('id', userId)

      if (error) throw error
    } catch (error) {
      console.error('Error updating user role:', error)
      throw error
    }
  }

  async deleteUser(userId: string): Promise<void> {
    if (!supabaseAdmin) {
      throw new Error('Supabase admin client is not configured')
    }

    try {
      // Note: In a real implementation, you might want to soft delete
      // or archive user data instead of hard deletion
      const { error } = await supabaseAdmin
        .from('profiles')
        .delete()
        .eq('id', userId)

      if (error) throw error
    } catch (error) {
      console.error('Error deleting user:', error)
      throw error
    }
  }

  async updateUserSubscription(userId: string, subscriptionStatus: 'freemium' | 'professional' | 'studio'): Promise<void> {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ subscription_status: subscriptionStatus })
        .eq('id', userId)

      if (error) throw error
    } catch (error) {
      console.error('Error updating user subscription:', error)
      throw error
    }
  }

  async suspendUser(userId: string): Promise<void> {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    try {
      // Add a suspended flag to the user profile
      const { error } = await supabase
        .from('profiles')
        .update({ 
          suspended: true,
          suspended_at: new Date().toISOString()
        })
        .eq('id', userId)

      if (error) throw error
    } catch (error) {
      console.error('Error suspending user:', error)
      throw error
    }
  }

  async unsuspendUser(userId: string): Promise<void> {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          suspended: false,
          suspended_at: null
        })
        .eq('id', userId)

      if (error) throw error
    } catch (error) {
      console.error('Error unsuspending user:', error)
      throw error
    }
  }

  async getUserActivity(userId: string) {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    try {
      // Get user's recent activity across different tables
      const [shootsResult, invoicesResult, clientsResult] = await Promise.all([
        supabase
          .from('shoots')
          .select('id, title, created_at, status')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(10),
        supabase
          .from('invoices')
          .select('id, invoice_number, total_amount, created_at, status')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(10),
        supabase
          .from('clients')
          .select('id, name, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(10)
      ])

      return {
        recentShoots: shootsResult.data || [],
        recentInvoices: invoicesResult.data || [],
        recentClients: clientsResult.data || []
      }
    } catch (error) {
      console.error('Error fetching user activity:', error)
      return {
        recentShoots: [],
        recentInvoices: [],
        recentClients: []
      }
    }
  }

  async searchUsers(query: string, limit: number = 20) {
    if (!supabaseAdmin) {
      throw new Error('Supabase admin client is not configured')
    }

    try {
      const { data, error } = await supabaseAdmin
        .from('profiles')
        .select(`
          id,
          email,
          first_name,
          last_name,
          created_at,
          subscription_status,
          role
        `)
        .or(`email.ilike.%${query}%,first_name.ilike.%${query}%,last_name.ilike.%${query}%`)
        .limit(limit)

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error searching users:', error)
      return []
    }
  }

  async getUserStats(userId: string) {
    if (!supabase) {
      throw new Error('Supabase is not configured')
    }

    try {
      const [shootsCount, invoicesCount, clientsCount, totalRevenue] = await Promise.all([
        supabase
          .from('shoots')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId),
        supabase
          .from('invoices')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId),
        supabase
          .from('clients')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId),
        supabase
          .from('invoices')
          .select('total_amount')
          .eq('user_id', userId)
          .eq('status', 'paid')
      ])

      const revenue = totalRevenue.data?.reduce((sum, invoice) => sum + (invoice.total_amount || 0), 0) || 0

      return {
        totalShoots: shootsCount.count || 0,
        totalInvoices: invoicesCount.count || 0,
        totalClients: clientsCount.count || 0,
        totalRevenue: revenue
      }
    } catch (error) {
      console.error('Error fetching user stats:', error)
      return {
        totalShoots: 0,
        totalInvoices: 0,
        totalClients: 0,
        totalRevenue: 0
      }
    }
  }
}

export const adminUserService = new AdminUserService() 